import {
    ChangeDetectionStrategy,
    Component,
    effect,
    ElementRef,
    inject,
    input,
    Renderer2,
    ViewEncapsulation
} from '@angular/core';
import { useLanguage } from '@sona/angular-util-common';
import { IAuditDetails, LighthouseFullPageScreenshot, LighthousePartyEntity } from '@sona/shared-scan-model-common';

import { SonaAuditDetailsService } from './services/audit-details.service';

/**
 * Displays an instance of audit details
 */
@Component({
    selector: 'sona-audit-details',
    standalone: true,
    templateUrl: './audit-details.component.html',
    styleUrls: ['./audit-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class SonaAuditDetailsComponent {
    private readonly _locale = useLanguage();
    private readonly _detailsService = inject(SonaAuditDetailsService, { optional: true }) ?? new SonaAuditDetailsService(inject(ElementRef));
    private readonly _renderer = inject(Renderer2);

    readonly auditDetails = input.required<IAuditDetails | object>();
    readonly entities = input<LighthousePartyEntity[]>();
    readonly fullPageScreenshot = input<LighthouseFullPageScreenshot>();

    constructor() {
        const elementRef = inject(ElementRef);
        const element = elementRef.nativeElement;

        effect(() => {
            const entities = this.entities();
            const fullPageScreenshot = this.fullPageScreenshot();

            if (entities) {
                this._detailsService.updateEntities(entities);
            }

            if (fullPageScreenshot) {
                this._detailsService.updateFullPageScreenshot(fullPageScreenshot);
            }
        });

        effect(() => {
            // we want to react to changes to the fullPageScreenshot
            this._detailsService.fullPageScreenshot();

            for (const child of element.childNodes) {
                this._renderer.removeChild(element, child);
            }

            const detailHtml = this._detailsService.render(
                this.auditDetails(),
                this._locale()
            );

            if (detailHtml) {
                detailHtml.classList.add('lh-details');
                this._renderer.appendChild(element, detailHtml);
            }
        });
    }
}
